import { backButton } from '../components/branding.js';
import { renderAppScreen } from '../components/shell.js';
import { ICONS } from '../components/icons.js';

/** Numbered steps — the recommended way through a single topic. */
const STEPS = [
  {
    title: 'Wybierz rozdział i temat',
    body:
      'Na ekranie głównym rozdziały opisują sytuacje z życia zespołu — standup, code ' +
      'review, DevOps, testy. Zacznij od tematu, który spotkasz w pracy najbliżej.',
  },
  {
    title: 'Dopasuj poziom',
    body:
      'Ten sam dialog czeka w dwóch wariantach: A1/A2 (krótsze zdania) i B1/B2 ' +
      '(bogatszy zasób słów). Przełączasz je pigułkami w dolnym pasku — najpierw ' +
      'poziom, w którym rozumiesz wszystko, potem trudniejszy.',
  },
  {
    title: 'Najpierw posłuchaj całości',
    body:
      'Duży przycisk play odtwarza cały dialog, a ekran sam przewija się do zdania, ' +
      'które właśnie słyszysz. Pierwsze podejście zrób bez czytania — tylko ze słuchu.',
  },
  {
    title: 'Potem zdanie po zdaniu',
    body:
      'Każde zdanie ma własny przycisk odtwarzania. Powtarzaj na głos zaraz po ' +
      'nagraniu, naśladując rytm i intonację — to ćwiczy wymowę szybciej niż czytanie w myślach.',
  },
  {
    title: 'Wyłap zwroty',
    body:
      'Sekcja „Przydatne zwroty” zbiera to, co przeniesiesz do własnych wypowiedzi: ' +
      'początki zdań i idiomy. Wybierz dwa lub trzy i użyj ich na najbliższym spotkaniu.',
  },
  {
    title: 'Domknij temat',
    body:
      'Gdy dialog dojdzie do końca z dużego przycisku play, temat dostaje zielony ' +
      'znaczek, a rozdział — wyższy procent postępu. Postęp liczy się na tym urządzeniu.',
  },
];

/** Short habits that decide whether nauka się utrzyma. */
const HABITS = [
  {
    title: 'Krótko, ale codziennie',
    body: '10 minut dziennie działa lepiej niż godzina raz w tygodniu.',
  },
  {
    title: 'Wracaj do tego samego dialogu',
    body: 'Trzecie przesłuchanie daje więcej niż pierwsze przesłuchanie trzech nowych.',
  },
  {
    title: 'Mów na głos',
    body: 'Nawet szeptem. Zwrot wypowiedziany zostaje w pamięci, przeczytany — nie.',
  },
  {
    title: 'Zbieraj ulubione',
    body: 'Tematy z sercem trafiają do „Ulubionych” — to Twoja lista powtórek.',
  },
  {
    title: 'Ucz się offline',
    body: 'Raz otwarty temat zostaje na urządzeniu razem z audio — dojazd też się liczy.',
  },
];

/**
 * One numbered step — its own cream card, the same surface a chapter or topic row
 * sits on. The steps used to be rows of one list separated by hairlines; the
 * cards carry that separation without a border.
 */
function stepRow({ title, body }, index) {
  return `
    <li class="flex gap-4 rounded-2xl bg-cream p-4">
      <span class="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-lilac text-sm font-semibold text-brand">
        ${index + 1}
      </span>
      <div class="min-w-0">
        <h3 class="text-sm font-semibold text-neutral-900">${title}</h3>
        <p class="mt-1 text-sm leading-relaxed text-neutral-600">${body}</p>
      </div>
    </li>`;
}

/**
 * One habit. These stay rows rather than cards: they all live inside a single
 * mist card (see below), so a second stack of cream cards can't be mistaken for
 * more steps.
 */
function habitRow({ title, body }) {
  return `
    <li class="flex gap-3">
      <span class="mt-0.5 shrink-0 text-brand">${ICONS.check('h-4 w-4')}</span>
      <div class="min-w-0">
        <h3 class="text-sm font-medium text-neutral-900">${title}</h3>
        <p class="mt-0.5 text-sm leading-relaxed text-neutral-600">${body}</p>
      </div>
    </li>`;
}

/** "Jak się uczyć?" — static guide, reachable from the drawer menu. */
export function renderHowToLearn(mount) {
  renderAppScreen(mount, {
    active: 'how-to-learn',
    leading: backButton(),
    contentHtml: `
      <div class="flex flex-1 flex-col py-10">
        <h1 class="text-2xl font-semibold tracking-tight text-neutral-900">Jak się uczyć?</h1>
        <p class="mt-2 text-sm leading-relaxed text-neutral-600">
          Materiał to dialogi z codziennej pracy zespołu — nie listy słówek. Poniżej
          sprawdzony sposób, żeby przełożyć je na to, co powiesz na spotkaniu.
        </p>

        <section class="mt-8">
          <h2 class="text-xs font-semibold uppercase tracking-wide text-neutral-500">Krok po kroku</h2>
          <ol class="mt-3 flex flex-col gap-3">
            ${STEPS.map(stepRow).join('')}
          </ol>
        </section>

        <section class="mt-8">
          <h2 class="text-xs font-semibold uppercase tracking-wide text-neutral-500">Nawyki, które robią różnicę</h2>
          <ul class="mt-3 flex flex-col gap-4 rounded-2xl bg-mist/40 p-4">
            ${HABITS.map(habitRow).join('')}
          </ul>
        </section>

        <div class="mt-8 rounded-2xl bg-sand/40 px-4 py-4">
          <p class="text-sm leading-relaxed text-neutral-800">
            Nie musisz rozumieć każdego słowa. Wystarczy, że po dialogu potrafisz
            powiedzieć jedno zdanie własnymi słowami — to jest cały cel jednej sesji.
          </p>
        </div>

        <a href="#/"
          class="btn-primary mt-8 w-full px-4 py-3">
          Przejdź do rozdziałów
        </a>
      </div>`,
  });
}
